import type {
  BucketAmounts,
  BucketBalances,
  GameHold,
  PlayerBucket,
  PlayerOperation,
} from "@pax/api-contract/accounting/v1alpha1";

/**
 * What one operation changed in a player's balances, and when it was recorded. `hold` names the
 * game and kind when the operation settled a hold, and in a game's holds, who paid.
 */
export interface BalanceChange {
  at: string;
  changes: BucketAmounts;
  hold?: {
    /** Null for a signed-in user's own hold, placed outside any game. */
    gameId: string | null;
    gameTitle: string | null;
    kind: string;
    payer?: { userId: string; name: string };
  };
}

/** An operation's changes, and the game and kind it charged for when it settled a hold. */
export function balanceChange(operation: PlayerOperation): BalanceChange {
  const held = operation.effectKind === "hold_settlement" ? operation.heldFor : null;
  return {
    at: operation.recordedAt,
    changes: operation.playerChanges,
    ...(held
      ? {
          hold: {
            gameId: held.gameId,
            gameTitle: held.gameTitle ?? null,
            kind: held.kind,
          },
        }
      : {}),
  };
}

/**
 * What a charged game hold took from each payer's balances, placed when the hold was, one change
 * per payer. Nothing for a hold that was not charged.
 */
export function gameHoldChanges(gameId: string, hold: GameHold): BalanceChange[] {
  const postings = hold.settlement?.entries.flatMap(entry => entry.postings) ?? [];
  return hold.payers.map(payer => {
    const moved = new Map<PlayerBucket, bigint>();
    for (const posting of postings) {
      if (posting.accountUserId !== payer.userId || !posting.bucket) continue;
      const signed =
        BigInt(posting.amountRaw) * (posting.side === "credit" ? BigInt(1) : BigInt(-1));
      moved.set(posting.bucket, (moved.get(posting.bucket) ?? BigInt(0)) + signed);
    }
    return {
      at: hold.placedAt,
      changes: Object.fromEntries([...moved].map(([bucket, raw]) => [bucket, raw.toString()])),
      hold: {
        gameId,
        gameTitle: null,
        kind: hold.kind,
        payer: { userId: payer.userId, name: payer.displayName },
      },
    };
  });
}

/** The cursor whose page of a game's holds starts with this hold. */
export const startingWith = (holdId: string) => (BigInt(holdId) + BigInt(1)).toString();

export type RawBalances = Record<PlayerBucket, bigint>;

/**
 * The player's balance in each bucket at each instant, worked back from `current` through every
 * change since. Each instant counts the changes recorded before it. Null before `knownFrom`, where
 * an older change may be missing.
 */
function balancesAt(
  current: BucketBalances,
  changes: readonly BalanceChange[],
  instants: readonly Date[],
  knownFrom: number,
): (RawBalances | null)[] {
  const newestFirst = [...changes].sort(
    (left, right) => Date.parse(right.at) - Date.parse(left.at),
  );
  const running = Object.fromEntries(
    Object.entries(current).map(([bucket, raw]) => [bucket, BigInt(raw)]),
  ) as RawBalances;
  const values = new Array<RawBalances | null>(instants.length).fill(null);
  let next = 0;
  for (let index = instants.length - 1; index >= 0; index--) {
    const instant = instants[index]?.valueOf() ?? 0;
    for (; next < newestFirst.length; next++) {
      const change = newestFirst[next];
      if (!change || Date.parse(change.at) < instant) break;
      for (const [bucket, raw] of Object.entries(change.changes) as [PlayerBucket, string][]) {
        running[bucket] -= BigInt(raw);
      }
    }
    if (instant >= knownFrom) values[index] = { ...running };
  }
  return values;
}

/**
 * The buckets a hold spends from, first to last, as `ledger._apply_spending_cascade` draws them.
 * Moderation comes before them all, for moderation holds only.
 */
export const SPEND_ORDER = [
  "daily",
  "signup",
  "sponsorship",
  "commission",
  "compensation",
  "subscription",
  "oneTimePurchase",
] as const satisfies readonly PlayerBucket[];

/** Every bucket but moderation, which the page's current balance leaves out too. */
export function spendable(balances: RawBalances) {
  const { moderation: _moderation, ...rest } = balances;
  return Object.values(rest).reduce((total, raw) => total + raw, BigInt(0));
}

/** The buckets that hold anything at any of these points, in the order the page lists them. */
export function heldBuckets(
  values: readonly (RawBalances | null)[],
  order: readonly PlayerBucket[],
) {
  return order.filter(bucket =>
    values.some(value => value !== null && value[bucket] !== BigInt(0)),
  );
}

/**
 * The player's balances from `from`, or `knownFrom` when later, up to `to`: the balance then, and
 * again just after each change, each holding until the next.
 */
export function balanceSteps(
  current: BucketBalances,
  changes: readonly BalanceChange[],
  from: number,
  to: number,
  knownFrom: number,
) {
  const start = Math.max(from, knownFrom);
  const times = changes.map(change => Date.parse(change.at)).filter(at => at >= start && at < to);
  const instants = [
    start,
    ...[...new Set(times)].sort((left, right) => left - right).map(at => at + 1),
  ];
  const values = balancesAt(
    current,
    changes,
    instants.map(at => new Date(at)),
    knownFrom,
  );
  return instants.flatMap((at, index) => {
    const balances = values[index];
    return balances ? [{ at, balances }] : [];
  });
}

/** What spending stacks by: the game, the player who paid, or the balance it came from. */
export type SpendBy = "game" | "player" | "bucket";

/**
 * What holds charged in each step starting at `starts`, the last ending at `end`, by `by`, as raw
 * amounts. `keys` lists what spent, most first, and `labels` names each game (null once deleted) or
 * player. Only hold settlements count as spending.
 */
export function spendBySteps(
  changes: readonly BalanceChange[],
  starts: readonly Date[],
  end: number,
  by: SpendBy,
) {
  const steps = starts.map(() => new Map<string, bigint>());
  const labels = new Map<string, string | null>();
  const totals = new Map<string, bigint>();
  const first = starts[0]?.valueOf() ?? end;
  for (const change of changes) {
    const at = Date.parse(change.at);
    if (!change.hold || at < first || at >= end) continue;
    let index = starts.length - 1;
    while (index > 0 && (starts[index]?.valueOf() ?? 0) > at) index -= 1;
    const step = steps[index];
    if (!step) continue;
    const { gameId, gameTitle, payer } = change.hold;
    const payerId = payer?.userId ?? "";
    const gameKey = gameId ?? "";
    if (by === "game") labels.set(gameKey, gameId ? gameTitle : "Outside games");
    if (by === "player") labels.set(payerId, payer?.name ?? null);
    for (const [bucket, raw] of Object.entries(change.changes) as [PlayerBucket, string][]) {
      const key = by === "game" ? gameKey : by === "player" ? payerId : bucket;
      const spent = -BigInt(raw);
      step.set(key, (step.get(key) ?? BigInt(0)) + spent);
      totals.set(key, (totals.get(key) ?? BigInt(0)) + spent);
    }
  }
  const spent = (key: string) => totals.get(key) ?? BigInt(0);
  return {
    steps: steps.map(step => (key: string) => (step.get(key) ?? BigInt(0)).toString()),
    labels,
    keys: [...totals.keys()].sort((left, right) =>
      spent(left) === spent(right) ? 0 : spent(left) > spent(right) ? -1 : 1,
    ),
    total: [...totals.values()].reduce((total, raw) => total + raw, BigInt(0)).toString(),
  };
}

/** The span of time a click on the balance graph picked, from `from` until just before `to`. */
export interface HistoryWindow {
  from: number;
  to: number;
}
